

import { useCallback, useMemo } from 'react';
import TextProperty from './NodeProperties/TextProperty'
import MultiLineTextProperty from './NodeProperties/MultiLineTextProperty'
import NumberProperty from './NodeProperties/NumberProperty'
import SelectProperty from './NodeProperties/SelectProperty'
import MultiSelectProperty from './NodeProperties/MultiSelectProperty'
import { Box, Typography, Divider } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListSubheader from '@mui/material/ListSubheader';

import useStore, { CustomReactFlowNode } from '../store';

import {
    NumberProperty as INumberProperty,
    SelectProperty as ISelectProperty,
    MultiSelectProperty as IMultiSelectProperty,
    TextProperty as ITextProperty,
    MultiLineTextProperty as IMultiLineTextProperty,
    SliderProperty as ISliderProperty,
    BooleanProperty as IBooleanProperty,
    PathProperty as IPathProperty,
    MediaProperty as IMediaProperty,
    TagListProperty as ITagListProperty,
    PropertyEvent
} from '../peregrineFlowInterfaces';
import { socket } from '../socket';
import BooleanProperty from './NodeProperties/BooleanProperty';
import SliderProperty from './NodeProperties/SliderProperty';
import PathProperty from './NodeProperties/PathProperty';
import SpacerProperty from './NodeProperties/SpacerProperty';
import MediaProperty from './NodeProperties/MediaProperty';
import TagListProperty from './NodeProperties/TagListProperty';

export type IProperty = INumberProperty | ISelectProperty | IMultiSelectProperty | ITextProperty | IMultiLineTextProperty | ISliderProperty | IBooleanProperty | IPathProperty | IMediaProperty | ITagListProperty;
export type OnPropertyChanged = (prop: IProperty) => void;
export type NodePropertyProp = {
    property: IProperty;
    onChange?: OnPropertyChanged
}


interface Indexable {
    [key: string]: any;
}

const propertyTypes = {
    select: SelectProperty,
    multiselect: MultiSelectProperty,
    slider: SliderProperty,
    text: TextProperty,
    multilinetext: MultiLineTextProperty,
    path: PathProperty,
    number: NumberProperty,
    boolean: BooleanProperty,
    spacer: SpacerProperty,
    media: MediaProperty,
    taglist: TagListProperty,
} as Indexable;

function NodePropertyPane() {
    const selectedNodes = useStore((state) => state.selectedNodes);

    const handleChange = useCallback((node: CustomReactFlowNode, p: IProperty) => {
        const event = {
            event_type: "update",
            node: node.data,
            property: p
        } as unknown as PropertyEvent;

        socket.emit("property", event);
    }, []);


    const propertyFactory = useCallback((node: CustomReactFlowNode, prop: IProperty) => {
        const key = `${node.id}-${prop.label}`

        if (propertyTypes.hasOwnProperty(prop.data_type)) {
            const Component = propertyTypes[prop.data_type];
            // each *Property changes the values of a node property in-place!
            return (
                <Component key={key} property={prop} onChange={(p: IProperty) => handleChange(node, p)} />
            )
        } else {
            return (
                <Box key={key}>{prop.data_type} not supported!</Box>
            )
        }
    }, [handleChange]);

    const parseSelectedNodeProperties = useCallback((node: CustomReactFlowNode) => {
        return node.data.properties.map((prop, index) => {
            const key = `${node.id}-prop-${prop.label}-${index}`
            return (
                prop.data_type !== 'spacer' ? (
                    <ListItem key={key}>
                        {propertyFactory(node, prop)}
                    </ListItem>) : (<div key={key}>{propertyFactory(node, prop)}</div>)
            )
        });
    }, [propertyFactory]);

    const parseSelectedNodes = useMemo(() => {
        return selectedNodes.map((node: CustomReactFlowNode, index: Number) => {
            const key = `${node.id}-${index}`
            return (
                <List
                    sx={{ bgcolor: 'background.paper', padding: 1 }}
                    key={key}
                    subheader={
                        <ListSubheader key={`${key}-header`} style={{ zIndex: 5 }}>
                            <Typography variant="h6">{node.data.display_name}</Typography>
                            <Divider />
                        </ListSubheader>
                    }>
                    {parseSelectedNodeProperties(node)}
                </List>
            )
        })
    }, [selectedNodes, parseSelectedNodeProperties]);

    return (
        <div className="node-property-pane" style={{ position: "relative", height: "100%", width: "100%", overflow: "auto" }}>
            <Box sx={{ padding: 1 }}>
                {selectedNodes.length === 0 ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: 'text.secondary',
                        textAlign: 'center',
                        p: 3
                    }}>
                        <Typography variant="h6" gutterBottom>
                            No Node Selected
                        </Typography>
                        <Typography variant="body2">
                            Select a node in the editor to view and edit its properties.
                        </Typography>
                    </Box>
                ) : (
                    parseSelectedNodes
                )}
            </Box>
        </div >

    );
}

export default NodePropertyPane;
